import { useEffect, useRef, useState, useCallback } from 'react';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  BadgeCheck,
  Disc3,
  Music,
  Apple,
  Pause,
  Play,
  Share2,
  ShoppingCart,
  Sparkles,
  Star,
  Trash2,
  User,
  Video,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
  HandMetal,
  Loader2,
} from 'lucide-react';
import TrackDeepDive from '@/components/TrackDeepDive';
import ArtistDeepDive from '@/components/ArtistDeepDive';
import VideoWallModal from '@/components/VideoWallModal';
import BuyVendorSheet from '@/components/BuyVendorSheet';
import DiscoverSimilarModal from '@/components/DiscoverSimilarModal';
import ShareDialog from '@/components/ShareDialog';
import PassModal from '@/components/PassModal';
import PassProvenanceBadge from '@/components/PassProvenanceBadge';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import {
  getAppleMusicTrackUrl,
  hasAppleMusicDirectLink,
} from '@/lib/appleMusic';
import {
  BENTO_BTN_VARIANTS,
  BENTO_PARENT_VARIANTS,
  makePeekShellVariants,
  MODAL_DIALOG_MOTION_CLASSES,
  PEEK_ART_MOTION,
} from '@/lib/motion';
import { useTriggerFocusReturn } from '@/lib/useTriggerFocusReturn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CANONICAL_BUCKETS } from '@/lib/genres';

const SOURCE_LABEL = {
  spotify: 'Spotify',
  shazam: 'Shazam',
  apple_music: 'Apple Music',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube_music: 'YouTube Music',
  soundcloud: 'SoundCloud',
  bandcamp: 'Bandcamp',
  beatport: 'Beatport',
  album_art: 'Album art',
  poster: 'Poster',
  ai_discovery: 'AI Find',
  other: 'Other',
};

// Shared hover treatment for every button in the action bento.
// A soft teal ring appears on hover/focus so the user always knows
// which tile they're pointing at. `transition-shadow` (not `all`) keeps
// the animation compliant with the design guidelines and cheap to paint.
// The ring is inset so it never causes layout shift on adjacent tiles.
const ACTION_BTN_HOVER =
  'transition-shadow duration-150 hover:ring-2 hover:ring-inset hover:ring-[hsl(var(--primary))]/40 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--primary))]/60';

// framer-motion-enhanced Button so each action tile in the bento grid
// can carry its own `variants` + `custom` (row index) props without a
// wrapper motion.div for every button. Works with shadcn Button's
// forwardRef signature. `motion.create()` replaces the deprecated
// `motion()` factory. Timing tokens + variant shapes live in
// `@/lib/motion` so any future modal shares the same choreography.
const MotionButton = motion.create(Button);

export default function MusicCardDetail({
  card,
  open,
  onClose,
  onMarkOwned,
  onUnown,
  onDelete,
  onGenreChange,
  onToggleWant = null,
  // --- deck navigation (optional; when provided, enables peeks + arrows
  //     + keyboard/swipe navigation between adjacent cards in the same crate)
  deck = null,
  deckIndex = -1,
  onNavigate = null,
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [trackDeepOpen, setTrackDeepOpen] = useState(false);
  const [artistDeepOpen, setArtistDeepOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [passOpen, setPassOpen] = useState(false);
  const audioRef = useRef(null);
  const { user } = useAuth() || {};
  const storefront = user?.apple_music_storefront;
  // Focus-return round-trip: hand focus back to the tile that opened
  // us when the modal closes. Implementation lives in the shared hook
  // so TrackDeepDive / ArtistDeepDive get the same behaviour with one
  // import.
  const { handleCloseAutoFocus } = useTriggerFocusReturn(open);
  // Local card overrides for fields we backfill from the modal without
  // waiting for the parent to refetch (e.g. Apple Music preview URL).
  // Reset whenever the underlying card changes.
  const [localOverrides, setLocalOverrides] = useState({});
  useEffect(() => {
    setLocalOverrides({});
  }, [card?.id]);
  const effectiveCard = card ? { ...card, ...localOverrides } : card;
  // Precompute Apple Music deep-link so both the button and the
  // album-art tap open the same URL. Recomputed only when card or
  // storefront changes, not on every render.
  const appleMusicUrl = effectiveCard ? getAppleMusicTrackUrl(effectiveCard, storefront) : null;
  const appleMusicIsDirect = hasAppleMusicDirectLink(effectiveCard);
  // Preview backfill (Phase AM1.2). Only offered when the card is
  // missing a `preview_url` but has an Apple Music track id — the
  // backend can look it up for free via iTunes' public lookup API.
  const canBackfillPreview =
    !effectiveCard?.preview_url &&
    !!(effectiveCard?.apple_music_track_id || effectiveCard?.itunes_track_id);
  const [backfillingPreview, setBackfillingPreview] = useState(false);

  const handleBackfillPreview = useCallback(async () => {
    if (!card?.id || backfillingPreview) return;
    setBackfillingPreview(true);
    try {
      const { data } = await api.post(`/cards/${card.id}/apple-music/preview/backfill`);
      // Merge whatever the server returned onto local overrides so
      // the button immediately flips to Play + all Apple Music fields
      // reflect the enrichment.
      setLocalOverrides((prev) => ({ ...prev, ...(data || {}) }));
      if (data?.preview_url) {
        toast.success('Preview loaded from Apple Music');
      } else {
        toast.info('No Apple Music preview available for this track');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to fetch preview');
    } finally {
      setBackfillingPreview(false);
    }
  }, [card?.id, backfillingPreview]);

  // Silent preview auto-load (Phase AM1.3):
  // The very first time the user opens a card that has an Apple Music
  // track id but no preview URL, we quietly attempt the backfill in
  // the background. On success the Preview button flips to Play; on
  // failure we simply leave the "Fetch preview" fallback visible so
  // the user can still trigger it manually. No toasts either way —
  // this is a "just make it work" enhancement, not a UI event.
  const autoLoadedRef = useRef(new Set());
  useEffect(() => {
    if (!open || !card?.id) return;
    if (autoLoadedRef.current.has(card.id)) return;
    if (effectiveCard?.preview_url) return;
    const trackId = effectiveCard?.apple_music_track_id || effectiveCard?.itunes_track_id;
    if (!trackId) return;
    autoLoadedRef.current.add(card.id);
    // Fire-and-forget; use the API directly so we don't display any
    // success/failure toasts (the manual "Fetch preview" button owns
    // that flow instead).
    (async () => {
      try {
        const { data } = await api.post(`/cards/${card.id}/apple-music/preview/backfill`);
        if (data && (data.preview_url || data.album_art_url)) {
          setLocalOverrides((prev) => ({ ...prev, ...data }));
        }
      } catch (_e) {
        /* silent — user can still tap "Fetch preview" manually */
      }
    })();
  }, [
    open,
    card?.id,
    effectiveCard?.preview_url,
    effectiveCard?.apple_music_track_id,
    effectiveCard?.itunes_track_id,
  ]);
  // Direction of the last navigation, drives the motion.div slide direction.
  //   1  = next card (slide from right)
  //  -1  = previous card (slide from left)
  //   0  = initial / no direction
  const [navDir, setNavDir] = useState(0);
  // Track touch start position for swipe detection on mobile.
  const touchStartX = useRef(null);

  // Deck bookkeeping — safe when deck isn't provided.
  const deckReady = Array.isArray(deck) && deck.length > 0 && deckIndex >= 0 && !!onNavigate;
  const prevCard = deckReady && deckIndex > 0 ? deck[deckIndex - 1] : null;
  const nextCard = deckReady && deckIndex < deck.length - 1 ? deck[deckIndex + 1] : null;
  const hasPrev = !!prevCard;
  const hasNext = !!nextCard;

  // True whenever any *nested* modal is open — we suppress deck keyboard
  // shortcuts so ArrowLeft/Right doesn't accidentally hop tracks while the
  // user is deep in a buy sheet or a deep-dive panel.
  const anyNestedOpen = buyOpen || trackDeepOpen || artistDeepOpen || videoOpen || discoverOpen || shareOpen || passOpen;

  const goPrev = useCallback(() => {
    if (!deckReady || !hasPrev) return;
    setNavDir(-1);
    onNavigate(deck[deckIndex - 1], deckIndex - 1);
  }, [deckReady, hasPrev, deck, deckIndex, onNavigate]);

  const goNext = useCallback(() => {
    if (!deckReady || !hasNext) return;
    setNavDir(1);
    onNavigate(deck[deckIndex + 1], deckIndex + 1);
  }, [deckReady, hasNext, deck, deckIndex, onNavigate]);

  // Keyboard arrow navigation (desktop). Bound only while modal is open
  // and there is no nested modal that could also want the arrow keys.
  useEffect(() => {
    if (!open || !deckReady || anyNestedOpen) return undefined;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') {
        if (hasPrev) {
          e.preventDefault();
          goPrev();
        }
      } else if (e.key === 'ArrowRight') {
        if (hasNext) {
          e.preventDefault();
          goNext();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, deckReady, anyNestedOpen, hasPrev, hasNext, goPrev, goNext]);

  // Basic touch swipe detection on the modal body. Threshold tuned to feel
  // natural but not accidentally trigger during a vertical scroll.
  const handleTouchStart = (e) => {
    if (!deckReady) return;
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };
  const handleTouchEnd = (e) => {
    if (!deckReady || touchStartX.current == null) return;
    const endX = e.changedTouches[0]?.clientX ?? null;
    if (endX == null) return;
    const delta = endX - touchStartX.current;
    touchStartX.current = null;
    const THRESHOLD = 50; // px
    if (delta > THRESHOLD && hasPrev) goPrev();
    else if (delta < -THRESHOLD && hasNext) goNext();
  };

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!open && audioEl) {
      try {
        audioEl.pause();
      } catch (_) { /* Firefox can throw if media not yet loaded */ }
      setIsPlaying(false);
    }
    return () => {
      if (audioEl) {
        try {
          audioEl.pause();
        } catch (_) { /* noop */ }
      }
    };
  }, [open]);

  if (!card) return null;

  const togglePreview = () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    if (audioEl.paused) {
      // .play() returns a Promise that Firefox rejects strictly when the
      // media hasn't loaded or autoplay policy blocks. We swallow that to
      // avoid an unhandled promise rejection bubbling up as an error overlay.
      const result = audioEl.play();
      if (result && typeof result.then === 'function') {
        result
          .then(() => setIsPlaying(true))
          .catch(() => {
            // Don't surface the error — just keep button in non-playing state.
            setIsPlaying(false);
          });
      } else {
        setIsPlaying(true);
      }
    } else {
      try {
        audioEl.pause();
      } catch (_) { /* noop */ }
      setIsPlaying(false);
    }
  };

  const isOwned = card.status === 'owned';
  const spotifyTrackId = card.spotify?.track_id;
  const spotifyArtistId = card.spotify?.artist_id;
  const hasSpotify = !!spotifyTrackId;

  return (
    <>
      {/* MotionConfig with reducedMotion="user" makes every framer-motion
          descendant automatically respect `prefers-reduced-motion: reduce`
          — transforms (scale/rotate/x/y) are skipped, opacity animations
          are kept, so users who opt out still get the modal but without
          the vestibular motion. Paired with the `motion-reduce:!duration-0`
          override on DialogContent below to cover the CSS-driven Radix
          animation layer. */}
      <MotionConfig reducedMotion="user">
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          /* DialogContent itself is invisible — Radix still owns its
             position:fixed centering + enter/exit animations. The visible
             "modal" is the .crate-neon-border two-div sandwich below.
             `!grid-cols-1 !gap-0` keeps Radix's `display:grid` from base
             but collapses it so our wrapper fills cleanly.

             Animation overrides:
              - `!duration-100`: replace Radix default 200ms with the
                snappy 100ms open+close the design brief calls for.
              - `!zoom-in-95`/`!zoom-out-95` are inherited (starts from
                95% scale) which reads as a clean "arrive/leave" without
                being noisy. Kept.
              - `!slide-in-from-*` classes are neutralised to 0 so the
                modal fades and scales in place instead of sliding
                diagonally from a corner. Both open and close directions
                are zeroed for symmetry.
              - `motion-reduce:!duration-0 motion-reduce:!animate-none`
                short-circuits the Radix CSS animation when the user has
                `prefers-reduced-motion: reduce` set, matching the
                framer-motion side handled by `MotionConfig` above. */
          className={`!bg-transparent !border-0 !p-0 !shadow-none max-w-md sm:max-w-lg w-[95vw] max-h-[92vh] rounded-2xl overflow-visible !grid-cols-1 !gap-0 ${MODAL_DIALOG_MOTION_CLASSES}`}
          data-testid="music-card-detail"
          onCloseAutoFocus={handleCloseAutoFocus}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{card.title || 'Music card'}</DialogTitle>
            <DialogDescription>Music card details and actions</DialogDescription>
          </DialogHeader>

          {/* Deck-peek layer — desktop-only preview of adjacent cards, sits
              behind the current card and hints there's more to browse.
              On mobile we hide the peeks (no room) and rely on swipe.

              Choreography (all values from @/lib/motion so any future
              modal shares the same spec):
                1. Modal shell scales+fades (0 → 100ms)
                2. Peek SHELLS fade+scale in (100 → 200ms) — bare tiles
                3. Peek ALBUM ART fades in on top (200 → 350ms) — makes
                   each tile feel like it "materialises from mask to art"
                4. Button bento cascades in (from 100ms, per-row stagger)

              Everything above is skipped when the user has set
              `prefers-reduced-motion: reduce` — MotionConfig handles
              transforms; the CSS side is guarded on DialogContent. */}
          {deckReady && hasPrev && (
            <motion.button
              type="button"
              onClick={goPrev}
              aria-label={`Previous track: ${prevCard?.title || ''}`}
              data-testid="music-card-detail-peek-prev"
              className="hidden lg:flex absolute top-1/2 right-full mr-5 w-48 aspect-square rounded-xl overflow-hidden bg-secondary items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]/60 border border-border/60 shadow-lg"
              {...makePeekShellVariants(-4)}
              title="Previous track"
            >
              <motion.div className="w-full h-full flex items-center justify-center" {...PEEK_ART_MOTION}>
                {prevCard?.album_art_url ? (
                  <img src={prevCard.album_art_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Disc3 className="w-14 h-14 text-muted-foreground/70" />
                )}
              </motion.div>
            </motion.button>
          )}
          {deckReady && hasNext && (
            <motion.button
              type="button"
              onClick={goNext}
              aria-label={`Next track: ${nextCard?.title || ''}`}
              data-testid="music-card-detail-peek-next"
              className="hidden lg:flex absolute top-1/2 left-full ml-5 w-48 aspect-square rounded-xl overflow-hidden bg-secondary items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]/60 border border-border/60 shadow-lg"
              {...makePeekShellVariants(4)}
              title="Next track"
            >
              <motion.div className="w-full h-full flex items-center justify-center" {...PEEK_ART_MOTION}>
                {nextCard?.album_art_url ? (
                  <img src={nextCard.album_art_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Disc3 className="w-14 h-14 text-muted-foreground/70" />
                )}
              </motion.div>
            </motion.button>
          )}

          {/* Nav arrow buttons — always visible on any screen size when deck
              navigation is available. On mobile they overlay the modal edge;
              on desktop they sit just outside the card. */}
          {deckReady && hasPrev && (
            <button
              type="button"
              onClick={goPrev}
              aria-label="Previous track"
              data-testid="music-card-detail-nav-prev"
              className="absolute top-1/2 -translate-y-1/2 -left-2 sm:-left-4 lg:-left-6 z-20 w-10 h-10 rounded-full bg-black/70 backdrop-blur-md text-white grid place-items-center border border-white/10 shadow-lg hover:bg-black/90 hover:scale-105 active:scale-95 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]/70"
              title="Previous track (←)"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {deckReady && hasNext && (
            <button
              type="button"
              onClick={goNext}
              aria-label="Next track"
              data-testid="music-card-detail-nav-next"
              className="absolute top-1/2 -translate-y-1/2 -right-2 sm:-right-4 lg:-right-6 z-20 w-10 h-10 rounded-full bg-black/70 backdrop-blur-md text-white grid place-items-center border border-white/10 shadow-lg hover:bg-black/90 hover:scale-105 active:scale-95 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]/70"
              title="Next track (→)"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}

          {/* Deck position indicator — anchored under the modal so it never
              overlaps content. Only rendered when deck is meaningful (2+). */}
          {deckReady && deck.length > 1 && (
            <div
              className="absolute left-1/2 -translate-x-1/2 -bottom-8 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-[10px] font-mono text-white/80"
              data-testid="music-card-detail-deck-indicator"
            >
              <span>{deckIndex + 1}</span>
              <span className="opacity-50">/</span>
              <span>{deck.length}</span>
            </div>
          )}

          {/* Pattern A: outer = animated gradient bg, inner = solid card on top.
              The 2px padding on .crate-neon-border is the visible "border" ring.
              Open/close fade+scale is handled by DialogContent's own CSS
              (Radix + tailwindcss-animate) — we override to 100ms and kill
              the slide-in-from-corner transforms via className above so it
              reads as a pure centered scale+fade. */}
          <div className="crate-neon-border rounded-2xl shadow-2xl">
            <div
              className="bg-card rounded-[14px] max-h-[calc(92vh-4px)] overflow-y-auto"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <AnimatePresence mode="wait" initial={false} custom={navDir}>
                <motion.div
                  key={card.id || `${card.artist}-${card.title}`}
                  custom={navDir}
                  initial={{ opacity: 0, x: navDir * 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -navDir * 24 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                >
              <div className="p-4">
            {/* Artwork — compact 160px so all info + actions fit without scrolling.
                Wrapped in a relative container so the Want star can float
                just outside the artwork's left edge (better discoverability
                than sitting on top of the album art itself). */}
            <div className="relative w-40 mx-auto mb-3">
              {/* Want-list star toggle — anchored outside the artwork's left
                  edge so it doesn't obscure album art. Filled amber when
                  active, neutral card style when off. */}
              {onToggleWant && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onToggleWant(card); }}
                  aria-label={card.want ? 'Remove from want list' : 'Add to want list'}
                  aria-pressed={!!card.want}
                  title={card.want ? 'On your want list' : 'Add to want list'}
                  data-testid="music-card-detail-want-toggle"
                  className={`absolute top-1 -left-11 sm:-left-12 z-10 w-9 h-9 rounded-full grid place-items-center border transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]/70 shadow-md ${
                    card.want
                      ? 'bg-[#FFB24A] text-[#0B0F14] border-[#FFB24A] hover:brightness-110'
                      : 'bg-card text-muted-foreground border-border hover:text-[hsl(var(--foreground))] hover:border-[#FFB24A]/60 hover:bg-[#FFB24A]/5'
                  }`}
                >
                  <Star className={`w-4 h-4 ${card.want ? 'fill-current' : ''}`} />
                </button>
              )}
              <div className="relative w-40 h-40 rounded-xl overflow-hidden bg-secondary grid place-items-center">
                {card.album_art_url ? (
                  <img
                    src={card.album_art_url}
                    alt={`${card.title} album art`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Disc3 className="w-12 h-12 text-muted-foreground" />
                )}
                {/* Tap album art to open Apple Music (Phase AM1.1).
                    Positioned as a full-cover button so we don't wrap
                    the img (keeps deck swipe / Want star click zones
                    free of extra event bubbling). The hover overlay
                    reveals the intent — never shown on touch devices
                    thanks to `group-hover:` gating. */}
                {appleMusicUrl && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(appleMusicUrl, '_blank');
                    }}
                    aria-label={
                      appleMusicIsDirect
                        ? 'Open this track in Apple Music'
                        : 'Search this track on Apple Music'
                    }
                    title={
                      appleMusicIsDirect
                        ? 'Open in Apple Music'
                        : 'Search on Apple Music'
                    }
                    data-testid="music-card-detail-cover-apple-music"
                    className="group absolute inset-0 flex items-end justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]/70 focus-visible:ring-inset"
                  >
                    <span
                      className="pointer-events-none w-full text-center text-[10px] font-mono uppercase tracking-wider py-1.5 bg-black/70 text-white backdrop-blur-sm opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-150 flex items-center justify-center gap-1.5"
                    >
                      <Apple className="w-3 h-3" />
                      {appleMusicIsDirect ? 'Open in Apple Music' : 'Search on Apple Music'}
                    </span>
                  </button>
                )}
                {isOwned && (
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-[var(--brand-lime)] text-[#0B0F14] hover:bg-[var(--brand-lime)] text-[10px] py-0 px-1.5">
                      <BadgeCheck className="w-3 h-3 mr-0.5" /> Owned
                    </Badge>
                  </div>
                )}
              </div>
            </div>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              {card.source_app && (
                <Badge variant="secondary" className="text-[10px] font-mono uppercase py-0 px-1.5">
                  {SOURCE_LABEL[card.source_app] || card.source_app}
                </Badge>
              )}
              {/* Clickable genre — opens a dropdown to move the track to a different crate */}
              {onGenreChange ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-[10px] uppercase py-0 px-1.5 h-5 rounded-md border border-border bg-background/40 text-foreground/90 hover:border-[hsl(var(--primary))]/50 hover:text-[hsl(var(--primary))] transition-colors"
                      data-testid="music-card-detail-genre-trigger"
                      aria-label="Change genre"
                    >
                      {card.genre_bucket || 'Set genre'}
                      <ChevronDown className="w-3 h-3 opacity-70" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="max-h-72 overflow-y-auto"
                    data-testid="music-card-detail-genre-menu"
                  >
                    {CANONICAL_BUCKETS.map((bucket) => {
                      const selected = bucket === card.genre_bucket;
                      return (
                        <DropdownMenuItem
                          key={bucket}
                          onSelect={(e) => {
                            // Prevent the default close-and-focus behaviour from
                            // racing with our async API call.
                            e.preventDefault();
                            if (!selected) onGenreChange(card, bucket);
                          }}
                          className="text-xs cursor-pointer"
                          data-testid={`genre-option-${bucket.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        >
                          <span className="flex-1">{bucket}</span>
                          {selected && (
                            <Check className="w-3.5 h-3.5 text-[hsl(var(--primary))] ml-2" />
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                card.genre_bucket && (
                  <Badge variant="outline" className="text-[10px] uppercase py-0 px-1.5">
                    {card.genre_bucket}
                  </Badge>
                )
              )}
            </div>
            <h2 className="font-display text-xl font-semibold leading-tight" data-testid="music-card-detail-title">
              {card.title || 'Untitled'}
            </h2>
            <p className="text-sm text-muted-foreground" data-testid="music-card-detail-artist">{card.artist || 'Unknown'}</p>
            {card.album && (
              <p className="text-xs text-muted-foreground/80">From: {card.album}</p>
            )}
            {/* Provenance — shown only when this card was Passed to the user */}
            {card.passed_from && (
              <div className="mt-2">
                <PassProvenanceBadge passedFrom={card.passed_from} />
              </div>
            )}

            {/* Audio features quick row (BPM / Key / energy) */}
            {(card.bpm_key || card.spotify?.audio_features) && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="music-card-audio-features">
                {card.bpm_key?.tempo && (
                  <Badge className="text-[10px] font-mono bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30 hover:bg-[hsl(var(--primary))]/15">
                    🥁 {Math.round(card.bpm_key.tempo)} BPM
                  </Badge>
                )}
                {card.bpm_key?.key_name && (
                  <Badge className="text-[10px] font-mono bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30 hover:bg-[hsl(var(--primary))]/15">
                    🎹 {card.bpm_key.key_name}
                    {card.bpm_key.scale === 'minor' ? 'm' : card.bpm_key.scale === 'major' ? '' : ''}
                  </Badge>
                )}
                {!card.bpm_key?.tempo && card.spotify?.audio_features?.tempo && (
                  <Badge variant="secondary" className="text-[10px] font-mono">
                    {Math.round(card.spotify.audio_features.tempo)} BPM
                  </Badge>
                )}
                {!card.bpm_key?.key_name && card.spotify?.audio_features?.key != null && (
                  <Badge variant="secondary" className="text-[10px] font-mono">
                    Key {['C','C♯','D','D♯','E','F','F♯','G','G♯','A','A♯','B'][card.spotify.audio_features.key] || '?'}
                    {card.spotify.audio_features.mode === 0 ? ' min' : ' maj'}
                  </Badge>
                )}
                {card.spotify?.audio_features?.energy != null && (
                  <Badge variant="outline" className="text-[10px] font-mono">
                    Energy {Math.round(card.spotify.audio_features.energy * 100)}%
                  </Badge>
                )}
                {card.bpm_key?.source && (
                  <span className="text-[9px] font-mono text-muted-foreground/60 self-center">
                    via {card.bpm_key.source === 'getsongbpm' ? 'GetSongBPM' : card.bpm_key.source === 'acousticbrainz' ? 'AcousticBrainz' : card.bpm_key.source}
                  </span>
                )}
              </div>
            )}

            {/* Hidden audio */}
            {effectiveCard.preview_url && (
              <audio
                ref={audioRef}
                src={effectiveCard.preview_url}
                onEnded={() => setIsPlaying(false)}
                preload="none"
              />
            )}

            {/* Action bento — grouped by verb-family for scannability:
                  Row 1: consume (watch / listen)
                  Row 2: open in platform (Spotify / Apple Music)
                  Row 3: go deeper (Track / Artist deep dive)
                  Row 4: transact (Ringtone / Buy)
                  Row 5: explore outward (Discover — full width)
                  Row 6: social (Share / Pass)

                Every button in this grid gets `ACTION_BTN_HOVER` — a
                subtle teal ring on hover — so the user always knows
                which tile they're pointing at. On the color-tinted
                buttons (Watch / Preview / Discover) the ring layers
                on top of their own hover bg-opacity bump, which reads
                as "confirmed press target" without looking noisy.

                Each button motion wrapper carries `custom={rowIndex}` so
                pairs in the same row animate together, and each row
                starts 20ms after the previous one — giving the bento
                a choreographed cascade after the modal scale settles
                (100ms delay before the first row appears). */}
            <motion.div
              className="grid grid-cols-2 gap-1.5 mt-3"
              variants={BENTO_PARENT_VARIANTS}
              initial="hidden"
              animate="visible"
            >
              {/* Row 1a — Watch music video. Red-accented flagship
                  media action; paired with Preview instead of hogging
                  the full row. The Video Wall modal handles both embed
                  and "Watch on YouTube" fallback. */}
              <MotionButton
                variants={BENTO_BTN_VARIANTS}
                custom={0}
                variant="secondary"
                className={`rounded-xl bg-[#FF0000]/15 text-[#FF6B6B] hover:bg-[#FF0000]/25 border border-[#FF0000]/30 ${ACTION_BTN_HOVER}`}
                data-testid="music-card-detail-video"
                onClick={() => setVideoOpen(true)}
              >
                <Video className="w-4 h-4 mr-2" /> Watch music video
              </MotionButton>
              {/* Row 1b — Preview. Mirrors Watch's tinted-with-border
                  treatment but in the primary teal so the "consume"
                  row reads as a matched pair (red = video, teal =
                  audio). Same visual weight both sides. Fallbacks
                  ("Fetch preview" + "No preview") keep the same shell
                  so the row never collapses to a solid button. */}
              {effectiveCard.preview_url ? (
                <MotionButton
                  variants={BENTO_BTN_VARIANTS}
                  custom={0}
                  variant="secondary"
                  onClick={togglePreview}
                  data-testid="music-card-detail-preview"
                  className={`rounded-xl bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/25 border border-[hsl(var(--primary))]/30 ${ACTION_BTN_HOVER}`}
                >
                  {isPlaying ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                  {isPlaying ? 'Stop' : 'Preview'}
                </MotionButton>
              ) : canBackfillPreview ? (
                <MotionButton
                  variants={BENTO_BTN_VARIANTS}
                  custom={0}
                  variant="secondary"
                  onClick={handleBackfillPreview}
                  disabled={backfillingPreview}
                  data-testid="music-card-detail-preview-backfill"
                  className={`rounded-xl bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/25 border border-[hsl(var(--primary))]/30 ${ACTION_BTN_HOVER}`}
                  title="Fetch a 30-second Apple Music preview"
                >
                  {backfillingPreview ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Apple className="w-4 h-4 mr-2" />
                  )}
                  Fetch preview
                </MotionButton>
              ) : (
                <MotionButton
                  variants={BENTO_BTN_VARIANTS}
                  custom={0}
                  variant="secondary"
                  disabled
                  data-testid="music-card-detail-preview"
                  className={`rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]/60 border border-[hsl(var(--primary))]/20 ${ACTION_BTN_HOVER}`}
                >
                  <Play className="w-4 h-4 mr-2" />
                  No preview
                </MotionButton>
              )}
              {/* Row 2 — Open in platform */}
              <MotionButton
                variants={BENTO_BTN_VARIANTS}
                custom={1}
                variant="secondary"
                onClick={() => {
                  if (hasSpotify) {
                    window.open(card.spotify?.external_url || card.spotify?.search_url || '#', '_blank');
                  } else {
                    window.open(card.spotify?.search_url || '#', '_blank');
                  }
                }}
                data-testid="music-card-detail-spotify"
                className={`rounded-xl ${ACTION_BTN_HOVER}`}
              >
                <Music className="w-4 h-4 mr-2" /> Open in Spotify
              </MotionButton>
              <MotionButton
                variants={BENTO_BTN_VARIANTS}
                custom={1}
                variant="secondary"
                onClick={() => {
                  // Deep-link priority handled by getAppleMusicTrackUrl helper.
                  if (appleMusicUrl) window.open(appleMusicUrl, '_blank');
                }}
                data-testid="music-card-detail-apple-music"
                className={`rounded-xl ${ACTION_BTN_HOVER}`}
                title={
                  appleMusicIsDirect
                    ? 'Open this track in Apple Music'
                    : 'Search on Apple Music'
                }
              >
                <Apple className="w-4 h-4 mr-2" /> Open in Apple Music
              </MotionButton>
              {/* Row 3 — Go deeper */}
              <MotionButton
                variants={BENTO_BTN_VARIANTS}
                custom={2}
                variant="secondary"
                onClick={() => {
                  if (hasSpotify) {
                    setTrackDeepOpen(true);
                  } else {
                    window.open(
                      card.mbid
                        ? `https://musicbrainz.org/recording/${card.mbid}`
                        : `https://musicbrainz.org/search?query=${encodeURIComponent((card.artist || '') + ' ' + (card.title || ''))}`,
                      '_blank'
                    );
                  }
                }}
                data-testid="music-card-detail-deepdive"
                className={`rounded-xl ${ACTION_BTN_HOVER}`}
              >
                <Sparkles className="w-4 h-4 mr-2" /> Track deep dive
              </MotionButton>
              <MotionButton
                variants={BENTO_BTN_VARIANTS}
                custom={2}
                variant="secondary"
                onClick={() => {
                  if (spotifyArtistId) {
                    setArtistDeepOpen(true);
                  } else {
                    window.open(
                      `https://open.spotify.com/search/${encodeURIComponent(card.artist || '')}`,
                      '_blank'
                    );
                  }
                }}
                data-testid="music-card-detail-artist-deepdive"
                className={`rounded-xl ${ACTION_BTN_HOVER}`}
              >
                <User className="w-4 h-4 mr-2" /> Artist deep dive
              </MotionButton>
              {/* Row 4 — Transact */}
              <MotionButton
                variants={BENTO_BTN_VARIANTS}
                custom={3}
                variant="secondary"
                onClick={() => window.open(card.ringtone_links?.itunes || '#', '_blank')}
                data-testid="music-card-detail-ringtone"
                className={`rounded-xl ${ACTION_BTN_HOVER}`}
              >
                <Music className="w-4 h-4 mr-2" /> Ringtone
              </MotionButton>
              <MotionButton
                variants={BENTO_BTN_VARIANTS}
                custom={3}
                variant="secondary"
                onClick={() => setBuyOpen(true)}
                data-testid="music-card-detail-buy"
                className={`rounded-xl ${ACTION_BTN_HOVER}`}
              >
                <ShoppingCart className="w-4 h-4 mr-2" /> Buy…
              </MotionButton>
              {/* Row 5 — Explore outward. Violet-accented so it reads
                  as its own distinct feature (separate identity from
                  the teal Preview and red Watch buttons). Violet fits
                  the AI-discovery / "magic sparkle" semantics of the
                  feature. Full-width for prominence. */}
              <MotionButton
                variants={BENTO_BTN_VARIANTS}
                custom={4}
                variant="secondary"
                onClick={() => setDiscoverOpen(true)}
                className={`rounded-xl col-span-2 bg-[#8B5CF6]/15 text-[#C4B5FD] hover:bg-[#8B5CF6]/25 border border-[#8B5CF6]/30 ${ACTION_BTN_HOVER}`}
                data-testid="music-card-detail-discover"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Discover similar tracks
              </MotionButton>
              {/* Row 6 — Social */}
              <MotionButton
                variants={BENTO_BTN_VARIANTS}
                custom={5}
                variant="outline"
                onClick={() => setShareOpen(true)}
                className={`rounded-xl h-9 ${ACTION_BTN_HOVER}`}
                data-testid="music-card-detail-share"
              >
                <Share2 className="w-4 h-4 mr-2" /> Share
              </MotionButton>
              <MotionButton
                variants={BENTO_BTN_VARIANTS}
                custom={5}
                variant="outline"
                onClick={() => setPassOpen(true)}
                className={`rounded-xl h-9 border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))] ${ACTION_BTN_HOVER}`}
                data-testid="music-card-detail-pass"
                title="Pass this track to another GroovLenser"
              >
                <HandMetal className="w-4 h-4 mr-2" /> Pass
              </MotionButton>
              {isOwned ? (
                <MotionButton
                  variants={BENTO_BTN_VARIANTS}
                  custom={6}
                  variant="ghost"
                  size="sm"
                  onClick={() => onUnown?.(card)}
                  className="rounded-lg justify-self-start px-2 h-8 text-xs text-[var(--brand-lime)] hover:bg-[#C8FF2E]/10 hover:text-[var(--brand-lime)]"
                  data-testid="music-card-detail-unown"
                >
                  <BadgeCheck className="w-3.5 h-3.5 mr-1.5" /> Owned — undo
                </MotionButton>
              ) : (
                <MotionButton
                  variants={BENTO_BTN_VARIANTS}
                  custom={6}
                  variant="ghost"
                  size="sm"
                  onClick={() => onMarkOwned?.(card)}
                  className="rounded-lg justify-self-start px-2 h-8 text-xs text-[var(--brand-lime)] hover:bg-[#C8FF2E]/10 hover:text-[var(--brand-lime)]"
                  data-testid="music-card-detail-mark-owned"
                >
                  <BadgeCheck className="w-3.5 h-3.5 mr-1.5" /> Mark Owned
                </MotionButton>
              )}
              <MotionButton
                variants={BENTO_BTN_VARIANTS}
                custom={6}
                variant="ghost"
                size="sm"
                onClick={() => onDelete?.(card)}
                className="rounded-lg justify-self-end px-2 h-8 text-xs text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                data-testid="music-card-detail-delete"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete card
              </MotionButton>
            </motion.div>
          </div>
                </motion.div>
              </AnimatePresence>
          {/* /.bg-card inner — closes the solid fill that masks the gradient */}
            </div>
          {/* /.crate-neon-border outer — closes the animated gradient wrapper */}
          </div>
        </DialogContent>
      </Dialog>
      </MotionConfig>

      {/* Buy vendor sheet */}
      <BuyVendorSheet
        buyLinks={card.buy_links || {}}
        open={buyOpen}
        onOpenChange={setBuyOpen}
      />

      {/* Track Deep Dive */}
      <TrackDeepDive
        trackId={spotifyTrackId}
        open={trackDeepOpen}
        onClose={() => setTrackDeepOpen(false)}
        seedCard={card}
      />

      {/* Artist Deep Dive */}
      <ArtistDeepDive
        artistId={spotifyArtistId}
        open={artistDeepOpen}
        onClose={() => setArtistDeepOpen(false)}
        seedName={card.artist}
      />

      {/* Music Video — new Video Wall modal in single-track mode. */}
      <VideoWallModal
        open={videoOpen}
        onOpenChange={(o) => setVideoOpen(!!o)}
        mode="single"
        track={card}
      />

      {/* AI Similar Tracks */}
      <DiscoverSimilarModal
        seedCard={card}
        open={discoverOpen}
        onClose={() => setDiscoverOpen(false)}
      />

      {/* Private share link (login-gated) */}
      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        kind="card"
        target={{
          id: card.id,
          title: card.title || 'Untitled track',
          subtitle: card.artist,
          image: card.album_art_url,
        }}
        payload={{ card_id: card.id }}
      />

      {/* Pass this track to another GroovLenser (in-person QR handoff) */}
      <PassModal
        open={passOpen}
        onClose={() => setPassOpen(false)}
        track={card}
        defaultMode="copy"
      />
    </>
  );
}
